import React, { useState, useEffect, useCallback } from 'react';

interface TerritoryStats {
  totalIslands: number;
  claimedIslands: number;
  unclaimedIslands: number;
  playerOwned: number;
  regimeOwned: number;
  ownershipByRegion: Record<string, { claimed: number; unclaimed: number }>;
  topOwners: Array<{ name: string; islandCount: number; region: string }>;
}

interface TerritoryOwnershipSummaryProps {
  apiBaseUrl: string;
  adminKey: string;
}

export const TerritoryOwnershipSummary: React.FC<TerritoryOwnershipSummaryProps> = ({ apiBaseUrl, adminKey }) => {
  const [stats, setStats] = useState<TerritoryStats | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [autoRefresh, setAutoRefresh] = useState<boolean>(true);

  const fetchStats = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await fetch(`${apiBaseUrl}/api/admin/prod/territory-stats`, {
        headers: { 'x-admin-key': adminKey },
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Error fetching territory stats:', err);
    } finally {
      setIsLoading(false);
    }
  }, [apiBaseUrl, adminKey]);

  useEffect(() => {
    fetchStats();
    if (!autoRefresh) return;
    const interval = setInterval(() => fetchStats(), 15000);
    return () => clearInterval(interval);
  }, [fetchStats, autoRefresh]);

  if (!stats) {
    return (
      <div style={{
        backgroundColor: '#1a1a2e',
        border: '1px solid #4a5568',
        borderRadius: 0,
        padding: 16,
        color: '#a0aec0',
        fontSize: 12,
        fontFamily: 'monospace',
        textTransform: 'uppercase',
      }}>
        LOADING TERRITORY DATA...
      </div>
    );
  }

  const claimPercentage = stats.totalIslands > 0 ? (stats.claimedIslands / stats.totalIslands) * 100 : 0;
  const playerPercentage = stats.claimedIslands > 0 ? (stats.playerOwned / stats.claimedIslands) * 100 : 0;

  return (
    <div style={{
      backgroundColor: '#1a1a2e',
      border: '1px solid #4a5568',
      borderRadius: 0,
      padding: 16,
      marginBottom: 16,
      fontFamily: 'monospace',
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16,
      }}>
        <h3 style={{ color: '#ffa500', fontSize: 14, margin: 0, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>[TERRITORY] OWNERSHIP SUMMARY</h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#a0aec0', fontSize: 11, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            Auto-refresh
          </label>
          <button
            onClick={() => fetchStats()}
            disabled={isLoading}
            style={{
              padding: '4px 8px',
              backgroundColor: isLoading ? '#2d3748' : '#4a5568',
              color: isLoading ? '#718096' : '#e2e8f0',
              border: '1px solid #4a5568',
              fontSize: 11,
              cursor: isLoading ? 'default' : 'pointer',
              borderRadius: 0,
            }}
          >
            {isLoading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Overview Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 12,
        marginBottom: 16,
      }}>
        <div style={{
          backgroundColor: '#0b1220',
          border: '1px solid #4a5568',
          borderRadius: 0,
          padding: 12,
        }}>
          <div style={{ color: '#a0aec0', fontSize: 10, marginBottom: 4 }}>Total Islands</div>
          <div style={{ color: '#22c55e', fontSize: 16, fontWeight: 600 }}>
            {stats.totalIslands}
          </div>
        </div>

        <div style={{
          backgroundColor: '#0b1220',
          border: '1px solid #4a5568',
          borderRadius: 0,
          padding: 12,
        }}>
          <div style={{ color: '#a0aec0', fontSize: 10, marginBottom: 4 }}>Claimed</div>
          <div style={{ color: '#3b82f6', fontSize: 16, fontWeight: 600 }}>
            {stats.claimedIslands} ({claimPercentage.toFixed(1)}%)
          </div>
        </div>

        <div style={{
          backgroundColor: '#0b1220',
          border: '1px solid #4a5568',
          borderRadius: 0,
          padding: 12,
        }}>
          <div style={{ color: '#a0aec0', fontSize: 10, marginBottom: 4 }}>Player Owned</div>
          <div style={{ color: '#8b5cf6', fontSize: 16, fontWeight: 600 }}>
            {stats.playerOwned} ({playerPercentage.toFixed(1)}%)
          </div>
        </div>

        <div style={{
          backgroundColor: '#0b1220',
          border: '1px solid #4a5568',
          borderRadius: 0,
          padding: 12,
        }}>
          <div style={{ color: '#a0aec0', fontSize: 10, marginBottom: 4 }}>Regime Owned</div>
          <div style={{ color: '#f59e0b', fontSize: 16, fontWeight: 600 }}>
            {stats.regimeOwned}
          </div>
        </div>
      </div>

      {/* Regional Breakdown */}
      <div style={{
        backgroundColor: '#0b1220',
        border: '1px solid #4a5568',
        borderRadius: 0,
        padding: 12,
        marginBottom: 16,
      }}>
        {stats.ownershipByRegion && Object.keys(stats.ownershipByRegion).length > 0 && (
          <>
            <div style={{ color: '#ffa500', fontSize: 12, fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              REGIONAL BREAKDOWN
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 12,
            }}>
              {Object.entries(stats.ownershipByRegion).map(([region, data]) => (
                <div key={region} style={{
                  padding: 8,
                  backgroundColor: '#1a1a2e',
                  borderRadius: 0,
                  borderLeft: '3px solid #fbbf24',
                }}>
                  <div style={{ color: '#a0aec0', fontSize: 10, marginBottom: 4, textTransform: 'capitalize' }}>
                    {region}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                    <span style={{ color: '#22c55e' }}>Claimed: {data.claimed}</span>
                    <span style={{ color: '#718096' }}>Unclaimed: {data.unclaimed}</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Top Owners */}
      <div style={{
        backgroundColor: '#0b1220',
        border: '1px solid #4a5568',
        borderRadius: 0,
        padding: 12,
      }}>
        <div style={{ color: '#ffa500', fontSize: 12, fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          👑 TOP TERRITORY OWNERS
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 80px 100px',
          gap: 8,
          padding: '8px 0',
          borderBottom: '1px solid #4a5568',
          marginBottom: 8,
          fontSize: 10,
          color: '#a0aec0',
        }}>
          <div>Owner</div>
          <div>Islands</div>
          <div>Region</div>
        </div>
        {stats.topOwners.slice(0, 10).map((owner, idx) => (
          <div
            key={idx}
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 80px 100px',
              gap: 8,
              padding: '8px 0',
              borderBottom: idx < stats.topOwners.length - 1 ? '1px solid #2d3748' : 'none',
              fontSize: 11,
              color: '#e2e8f0',
            }}
          >
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {owner.name}
            </div>
            <div style={{ color: '#22c55e', fontWeight: 600 }}>
              {owner.islandCount}
            </div>
            <div style={{ color: '#a0aec0', fontSize: 10, textTransform: 'capitalize' }}>
              {owner.region}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
